'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { sendGTMEvent } from '@next/third-parties/google'
import { HTTPError } from 'ky'
import {
  BadgeCheck,
  ChevronLeft,
  CircleCheck,
  Clock,
  FileDown,
  LoaderCircle,
  Lock,
  Mail,
  Shield,
  User,
  UserPlus,
} from 'lucide-react'
import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import { useTransition } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { sendEmailConfirm } from '@/actions/auth/send-email-confirm-action'
import { getProfileAction } from '@/actions/user/get-profile-action'
import AuthGoogleButton from '@/app/conta/login/_components/auth-google-button'
import { loginUser } from '@/app/conta/login/action'
import { mapUserDataForEnhancedMatch } from '@/components/@analitycs/map-user-data-for-enhanced-match'
import { buildGA4EventModel } from '@/components/@analitycs/push-ecommerce-event'

import { EmailInputWithSuggestions } from '@/components/global/email-input-with-suggestions'
import { PasswordInput } from '@/components/password-input'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { getStagingEmailErrorMessage } from '@/helpers/staging-email-validation'
import { bff_client } from '@/lib/apiClient'

const MIN_PASSWORD_LENGTH = 6
const TOAST_DURATION = 10_000
const HTTP_STATUS_FORBIDDEN = 403
const USER_ALREADY_REGISTERED_MESSAGE = 'User already registered.'

export const signUpFormSchema = z
  .object({
    full_name: z
      .string()
      .min(1, { message: 'O campo nome é obrigatório' })
      .regex(/^[A-Za-zá-úÁ-Ú\s]+$/, { message: 'Somente letras' }),
    email: z
      .string()
      .trim()
      .toLowerCase()
      .min(1, { message: 'O campo e-mail é obrigatorio' })
      .email({ message: 'E-mail inválido' }),
    // .refine(email => isEmailAllowedInStaging(email), {
    //   message: 'Cadastro não permitido no ambiente de TESTES',
    //   path: ['email'],
    // }),
    re_email: z
      .string()
      .trim()
      .toLowerCase()
      .min(1, { message: 'O campo confirmar e-mail é obrigatório' })
      .email({ message: 'E-mail inválido' }),
    password: z
      .string()
      .nonempty({ message: 'O campo senha é obrigatorio' })
      .min(MIN_PASSWORD_LENGTH, {
        message: 'Senha deve ter pelo menos 6 caracteres.',
      }),
    re_password: z
      .string()
      .nonempty({ message: 'O campo repita a senha é obrigatorio' })
      .min(MIN_PASSWORD_LENGTH, {
        message: 'Senha deve ter pelo menos 6 caracteres.',
      }),
  })
  .refine(data => data.password === data.re_password, {
    message: 'As senhas fornecidas não coincidem.',
    path: ['re_password'],
  })
  .refine(data => data.email === data.re_email, {
    message: 'Os e-mails não coincidem.',
    path: ['re_email'],
  })

type SignUpFormType = z.infer<typeof signUpFormSchema>

function handleSignUpError(error: unknown) {
  if (error instanceof HTTPError) {
    return error.response.json().then(response => {
      const status = error.response.status
      const errorMessage = response.messages?.error || response.message

      if (
        status === HTTP_STATUS_FORBIDDEN &&
        errorMessage === USER_ALREADY_REGISTERED_MESSAGE
      ) {
        toast.error('Este e-mail já possui uma conta cadastrada', {
          description:
            'Caso tenha esquecido sua senha, utilize a opção de recuperação.',
          duration: TOAST_DURATION,
        })
        return
      }

      if (
        status === HTTP_STATUS_FORBIDDEN &&
        errorMessage === getStagingEmailErrorMessage()
      ) {
        toast.error('Cadastro não permitido no ambiente de TESTES', {
          description: 'Clique no botão para ir para a página oficial',
          action: {
            label: 'Ir para a página oficial',
            onClick: () => {
              window.open(
                'https://soeducador.com.br/switch-experiment?service=zamply',
                '_blank'
              )
            },
          },
          duration: 30_000,
        })
        return
      }

      toast.error(errorMessage || 'Erro ao criar conta', {
        duration: TOAST_DURATION,
      })
    })
  }

  toast.error('Erro ao criar conta', {
    duration: TOAST_DURATION,
  })
}

// Etapas não críticas do pós-cadastro: a conta já foi criada e o login já
// ocorreu. Falhas aqui (ex.: analytics/GTM bloqueados pelo ITP do Safari no
// iOS) não devem sinalizar erro de cadastro para o usuário.
async function runPostSignUpSideEffects(params: {
  email: string
  fullName: string
  userData: ReturnType<typeof mapUserDataForEnhancedMatch>
}) {
  const { email, fullName, userData } = params

  try {
    const { data: profileResult } = await getProfileAction()
    const userId = profileResult?.data?.reference ?? ''

    await sendEmailConfirm({ email, fullName })

    const eventModel = await buildGA4EventModel({ userData })

    sendGTMEvent({
      event: 'sign_up',
      method: 'email',
      eventModel,
      user_id: userId,
      ...(eventModel.user_data && { user_data: eventModel.user_data }),
    })

    sendGTMEvent({
      event: 'login',
      method: 'email',
      eventModel,
      user_id: userId,
      ...(eventModel.user_data && { user_data: eventModel.user_data }),
    })

    sendGTMEvent({
      event: 'generate_lead',
      eventModel,
      user_id: userId,
      ...(eventModel.user_data && { user_data: eventModel.user_data }),
    })
  } catch {
    // Ignorado de propósito: perfil/e-mail de confirmação/analytics são
    // best-effort e não devem invalidar um cadastro já concluído.
  }
}

export default function SignUpForm() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const redirectUrl = searchParams.get('redirect_url')
  const [statusLoading, startTransition] = useTransition()

  const form = useForm<SignUpFormType>({
    resolver: zodResolver(signUpFormSchema),
    mode: 'onChange',
    defaultValues: {
      full_name: '',
      email: '',
      re_email: '',
      password: '',
      re_password: '',
    },
  })

  const { control, handleSubmit, reset } = form

  function onSubmit(formData: SignUpFormType) {
    startTransition(async () => {
      try {
        await bff_client.post('user/create', {
          json: {
            full_name: formData.full_name,
            email: formData.email,
            password: formData.password,
            re_password: formData.re_password,
          },
        })

        const userData = mapUserDataForEnhancedMatch({
          fullName: formData.full_name,
          email: formData.email,
        })

        await loginUser({
          identifier: formData.email,
          password: formData.password,
        })

        await runPostSignUpSideEffects({
          email: formData.email,
          fullName: formData.full_name,
          userData,
        })

        reset()

        toast.success('Cadastro criado com sucesso', {
          description: 'Aguarde o redirecionamento',
        })
        if (redirectUrl) {
          router.push(redirectUrl)
        } else {
          router.push('/app')
        }
      } catch (error: unknown) {
        await handleSignUpError(error)
      }
    })
  }

  const { email, full_name, re_email, password, re_password } =
    form.formState.errors

  const emailError = !!email?.message
  const fullNameError = !!full_name?.message
  const reEmailError = !!re_email?.message
  const passwordError = !!password?.message
  const rePasswordError = !!re_password?.message

  return (
    <div className="flex items-center justify-center">
      <div className="grid w-full max-w-6xl items-center gap-8 lg:grid-cols-2 lg:gap-12">
        <div className="hidden h-full space-y-6 rounded-xl bg-white p-6 shadow-lg lg:block lg:p-10">
          <div className="space-y-6">
            <Link
              className="flex items-center gap-0.5 font-medium text-[#737373] text-sm"
              href="/"
            >
              <ChevronLeft className="size-4" />
              Voltar para página inicial
            </Link>

            <div className="space-y-4">
              <h2 className="font-semibold text-4xl text-slate-800 leading-tight">
                Uma plataforma pensada <br />
                especialmente para você
              </h2>
              <p className="max-w-md text-base text-slate-600 leading-relaxed">
                Pensamos em todos os detalhes para tornar sua experiência
                incrível e simples com a gente. Comece sua jornada de
                aprendizado hoje mesmo.
              </p>
            </div>

            <div className="space-y-4 pt-4">
              {cards.map(card => (
                <div className="flex items-start gap-3" key={card.title}>
                  <div className="flex items-center justify-center p-1">
                    {card.icon}
                  </div>
                  <div>
                    <h3 className="font-normal text-slate-800 text-sm">
                      {card.title}
                    </h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-white/20 bg-white/80 p-6 shadow-lg backdrop-blur-xs lg:p-8">
          <div className="mx-auto space-y-6">
            <div className="flex items-center justify-center">
              <div className="inline-flex flex-1 items-center justify-center gap-1">
                <Link
                  className="rounded-xl bg-primary-500 px-3 py-1 font-medium text-sm text-white shadow-xs transition-all duration-300 ease-in-out hover:bg-primary-600 focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                  href="/conta/novo"
                  scroll={false}
                >
                  Cadastre-se
                </Link>

                <Link
                  className="rounded-xl border border-primary-100 px-3 py-1 font-medium text-gray-500 text-sm transition-all duration-300 ease-in-out hover:text-gray-700 focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                  href={
                    redirectUrl
                      ? `/conta/login?redirect_url=${encodeURIComponent(redirectUrl)}`
                      : '/conta/login'
                  }
                  scroll={false}
                >
                  Já possuo cadastro
                </Link>
              </div>
            </div>

            <Form {...form}>
              <form method="post"
                className="space-y-5"
                noValidate
                onSubmit={handleSubmit(onSubmit)}
              >
                <FormField
                  control={control}
                  name="full_name"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel className="flex items-center gap-2 font-medium text-slate-700 text-sm">
                        <User className="h-4 w-4" />
                        Nome Completo
                      </FormLabel>
                      <FormControl>
                        <Input
                          className="h-11 rounded-xl border-2 border-slate-200 px-4 text-sm transition-all duration-200 placeholder:text-slate-400 focus:border-primary-400 focus:ring-4 focus:ring-primary-100 data-[error=true]:border-red-500 data-[error=true]:focus:ring-red-100"
                          data-error={fullNameError}
                          disabled={statusLoading}
                          placeholder="Seu nome completo"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage className="text-red-600 text-xs" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel className="flex items-center gap-2 font-medium text-slate-700 text-sm">
                        <Mail className="h-4 w-4" />
                        E-mail
                      </FormLabel>
                      <FormControl>
                        <EmailInputWithSuggestions
                          className="h-11 rounded-xl border-2 border-slate-200 px-4 text-sm transition-all duration-200 placeholder:text-slate-400 focus:border-primary-400 focus:ring-4 focus:ring-primary-100 data-[error=true]:border-red-500 data-[error=true]:focus:ring-red-100"
                          data-error={emailError}
                          disabled={statusLoading}
                          onValueChange={v => {
                            form.setValue('email', v, {
                              shouldValidate: true,
                              shouldDirty: true,
                            })
                          }}
                          placeholder="Digite seu e-mail"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage className="text-red-600 text-xs" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="re_email"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel className="flex items-center gap-2 font-medium text-slate-700 text-sm">
                        <Mail className="h-4 w-4" />
                        Confirme seu e-mail
                      </FormLabel>
                      <FormControl>
                        <EmailInputWithSuggestions
                          className="h-11 rounded-xl border-2 border-slate-200 px-4 text-sm transition-all duration-200 placeholder:text-slate-400 focus:border-primary-400 focus:ring-4 focus:ring-primary-100 data-[error=true]:border-red-500 data-[error=true]:focus:ring-red-100"
                          data-error={reEmailError}
                          disabled={statusLoading}
                          onValueChange={v => {
                            form.setValue('re_email', v, {
                              shouldValidate: true,
                              shouldDirty: true,
                            })
                          }}
                          placeholder="Confirme seu e-mail"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage className="text-red-600 text-xs" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="password"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel className="flex items-center gap-2 font-medium text-slate-700 text-sm">
                        <Lock className="h-4 w-4" />
                        Senha
                      </FormLabel>
                      <FormControl>
                        <PasswordInput
                          className="h-11 rounded-xl border-2 border-slate-200 px-4 text-sm transition-all duration-200 placeholder:text-slate-400 focus:border-primary-400 focus:ring-4 focus:ring-primary-100 data-[error=true]:border-red-500 data-[error=true]:focus:ring-red-100"
                          data-error={passwordError}
                          disabled={statusLoading}
                          placeholder="Digite sua senha"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage className="text-red-600 text-xs" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="re_password"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel className="flex items-center gap-2 font-medium text-slate-700 text-sm">
                        <Shield className="h-4 w-4" />
                        Confirme a Senha
                      </FormLabel>
                      <FormControl>
                        <PasswordInput
                          className="h-11 rounded-xl border-2 border-slate-200 px-4 text-sm transition-all duration-200 placeholder:text-slate-400 focus:border-primary-400 focus:ring-4 focus:ring-primary-100 data-[error=true]:border-red-500 data-[error=true]:focus:ring-red-100"
                          data-error={rePasswordError}
                          disabled={statusLoading}
                          placeholder="Digite sua senha novamente"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage className="text-red-600 text-xs" />
                    </FormItem>
                  )}
                />

                <Button
                  className="h-11 w-full rounded-xl bg-primary-400 font-semibold text-white shadow-lg transition-all duration-200 hover:bg-primary-500 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={statusLoading}
                  type="submit"
                >
                  {statusLoading ? (
                    <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <UserPlus className="mr-2 h-4 w-4" />
                  )}
                  {statusLoading ? 'Criando conta...' : 'Continuar'}
                </Button>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-slate-200 border-t" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="bg-white px-4 font-medium text-slate-500">
                      ou
                    </span>
                  </div>
                </div>

                <AuthGoogleButton />
              </form>
            </Form>

            {/* Sign In Link */}
            <div className="text-center">
              <p className="text-slate-600 text-sm">
                Já tem uma conta?{' '}
                <Link
                  className="font-semibold text-primary-400 text-sm transition-colors duration-200 hover:text-primary-500"
                  href={
                    redirectUrl
                      ? `/conta/login?redirect_url=${encodeURIComponent(redirectUrl)}`
                      : '/conta/login'
                  }
                >
                  Faça login aqui
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const cards = [
  {
    title: 'Material de estudos disponibilizados gratuitamente',
    icon: <FileDown className="h-4 w-4 text-primary-400" />,
  },
  {
    title: 'Disponível 24 horas por dia',
    icon: <Clock className="h-4 w-4 text-primary-400" />,
  },
  {
    title: 'Certificados válidos em todo o Brasil',
    icon: <BadgeCheck className="h-4 w-4 text-primary-400" />,
  },
  {
    title: 'Plataforma 100% legalizada',
    icon: <CircleCheck className="h-4 w-4 text-primary-400" />,
  },
]
